import { FileActionButton } from "../file-action/file-action-controls";
import React, { useState, useRef, useCallback } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import { Toast } from "primereact/toast";
import { DataTableStateEvent } from "primereact/datatable";
import AppLoading from "../app-loading/app-loading";
import customerAnalyticsService from "../../services/customer-analytics/customer-analytics.service";
import {
  ParetoAnalysisData,
  ParetoAnalysisFilter,
  RFMSegmentationData,
  RFMSegmentationFilter,
  HighValueAtRiskData,
  HighValueAtRiskFilter,
} from "../../models/customer-analytics/customer-analytics.model";
import {
  exportParetoAnalysis,
  exportRFMSegmentation,
  exportHighValueAtRisk,
} from "./customer-analytics-export";
import ParetoSearch from "./pareto/pareto-search";
import ParetoDataTable from "./pareto/pareto-datatable";
import RFMSearch from "./rfm/rfm-search";
import RFMDataTable from "./rfm/rfm-datatable";
import ChurnSearch from "./churn/churn-search";
import ChurnDataTable from "./churn/churn-datatable";
import "../../assests/css/customer-analytics/customer-analytics.scss";

const DEFAULT_PAGE_SIZE = 50;

const defaultStartDate = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return d;
};

// Maps camelCase DataTable column field to PascalCase SP @SortBy parameter
const sortFieldToSpParam: Record<string, string> = {
  totalRevenue: "TotalRevenue",
  avgOrderValue: "AvgOrderValue",
  daysSinceLastOrder: "DaysSinceLastOrder",
  recency: "Recency",
  frequency: "Frequency",
  monetary: "Monetary",
  recencyScore: "RecencyScore",
  frequencyScore: "FrequencyScore",
  monetaryScore: "MonetaryScore",
  rfmTotal: "RFMTotal",
  lifetimeRevenue: "LifetimeRevenue",
  totalOrders: "TotalOrders",
};

const toSpSortBy = (field: string): string => {
  return sortFieldToSpParam[field] || field;
};

const CustomerAnalyticsMain: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Track which tabs have been loaded
  const loadedTabs = useRef<Set<number>>(new Set());

  // Pareto state
  const [paretoData, setParetoData] = useState<ParetoAnalysisData[]>([]);
  const [paretoTotal, setParetoTotal] = useState(0);
  const [paretoPage, setParetoPage] = useState(1);
  const [paretoPageSize, setParetoPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [paretoSortField, setParetoSortField] = useState("totalRevenue");
  const [paretoSortOrder, setParetoSortOrder] = useState<1 | -1>(-1);
  const paretoFilters = useRef<ParetoAnalysisFilter>({
    startDate: defaultStartDate(),
    endDate: new Date(),
    decile: null,
    daysSinceLastOrder: null,
    pageSize: DEFAULT_PAGE_SIZE,
    pageNumber: 1,
    sortBy: "TotalRevenue",
    sortOrder: "DESC",
  });

  // RFM state
  const [rfmData, setRfmData] = useState<RFMSegmentationData[]>([]);
  const [rfmTotal, setRfmTotal] = useState(0);
  const [rfmPage, setRfmPage] = useState(1);
  const [rfmPageSize, setRfmPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [rfmSortField, setRfmSortField] = useState("rfmTotal");
  const [rfmSortOrder, setRfmSortOrder] = useState<1 | -1>(-1);
  const rfmFilters = useRef<RFMSegmentationFilter>({
    startDate: defaultStartDate(),
    endDate: new Date(),
    recency: null,
    frequency: null,
    monetary: null,
    recencyScore: null,
    frequencyScore: null,
    monetaryScore: null,
    rfmTotal: null,
    customerSegment: null,
    pageSize: DEFAULT_PAGE_SIZE,
    pageNumber: 1,
    sortBy: "RFMTotal",
    sortOrder: "DESC",
  });

  // Churn state
  const [churnData, setChurnData] = useState<HighValueAtRiskData[]>([]);
  const [churnTotal, setChurnTotal] = useState(0);
  const [churnPage, setChurnPage] = useState(1);
  const [churnPageSize, setChurnPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [churnSortField, setChurnSortField] = useState("lifetimeRevenue");
  const [churnSortOrder, setChurnSortOrder] = useState<1 | -1>(-1);
  const churnFilters = useRef<HighValueAtRiskFilter>({
    startDate: defaultStartDate(),
    endDate: new Date(),
    daysSinceLastOrder: 60,
    pageSize: DEFAULT_PAGE_SIZE,
    pageNumber: 1,
    sortBy: "LifetimeRevenue",
    sortOrder: "DESC",
  });

  // Fetch functions
  const fetchPareto = useCallback(async () => {
    setLoading(true);
    try {
      const response = await customerAnalyticsService.getParetoAnalysis(
        paretoFilters.current
      );
      setParetoData(response.data || []);
      setParetoTotal(response.totalRecords || 0);
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch Pareto analysis",
        life: 3000,
      });
      setParetoData([]);
      setParetoTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRFM = useCallback(async () => {
    setLoading(true);
    try {
      const response = await customerAnalyticsService.getRFMSegmentation(
        rfmFilters.current
      );
      setRfmData(response.data || []);
      setRfmTotal(response.totalRecords || 0);
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch RFM segmentation",
        life: 3000,
      });
      setRfmData([]);
      setRfmTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchChurn = useCallback(async () => {
    setLoading(true);
    try {
      const response =
        await customerAnalyticsService.getHighValueCustomersAtRisk(
          churnFilters.current
        );
      setChurnData(response.data || []);
      setChurnTotal(response.totalRecords || 0);
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch at-risk customers",
        life: 3000,
      });
      setChurnData([]);
      setChurnTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Tab change handler - lazy load
  const handleTabChange = (index: number) => {
    setActiveTab(index);
    if (!loadedTabs.current.has(index)) {
      loadedTabs.current.add(index);
      if (index === 0) fetchPareto();
      else if (index === 1) fetchRFM();
      else if (index === 2) fetchChurn();
    }
  };

  // Load first tab on mount
  React.useEffect(() => {
    if (!loadedTabs.current.has(0)) {
      loadedTabs.current.add(0);
      fetchPareto();
    }
  }, [fetchPareto]);

  // Pareto handlers
  const handleParetoSearch = (filters: {
    startDate: Date;
    endDate: Date;
    decile: number | null;
    daysSinceLastOrder: number | null;
  }) => {
    paretoFilters.current = {
      ...paretoFilters.current,
      ...filters,
      pageNumber: 1,
    };
    setParetoPage(1);
    fetchPareto();
  };

  const handleParetoReset = () => {
    paretoFilters.current = {
      startDate: defaultStartDate(),
      endDate: new Date(),
      decile: null,
      daysSinceLastOrder: null,
      pageSize: paretoPageSize,
      pageNumber: 1,
      sortBy: "TotalRevenue",
      sortOrder: "DESC",
    };
    setParetoPage(1);
    setParetoSortField("totalRevenue");
    setParetoSortOrder(-1);
    fetchPareto();
  };

  const handleParetoPageChange = (event: DataTableStateEvent) => {
    const newPage = Math.floor((event.first || 0) / (event.rows || DEFAULT_PAGE_SIZE)) + 1;
    const newSize = event.rows || DEFAULT_PAGE_SIZE;
    setParetoPage(newPage);
    setParetoPageSize(newSize);
    paretoFilters.current = {
      ...paretoFilters.current,
      pageNumber: newPage,
      pageSize: newSize,
    };
    fetchPareto();
  };

  const handleParetoSort = (field: string, order: string) => {
    setParetoSortField(field);
    setParetoSortOrder(order === "ASC" ? 1 : -1);
    paretoFilters.current = {
      ...paretoFilters.current,
      sortBy: toSpSortBy(field),
      sortOrder: order,
      pageNumber: 1,
    };
    setParetoPage(1);
    fetchPareto();
  };

  // RFM handlers
  const handleRFMSearch = (filters: {
    startDate: Date;
    endDate: Date;
    recency: number | null;
    frequency: number | null;
    monetary: number | null;
    recencyScore: number | null;
    frequencyScore: number | null;
    monetaryScore: number | null;
    rfmTotal: number | null;
    customerSegment: string | null;
  }) => {
    rfmFilters.current = {
      ...rfmFilters.current,
      ...filters,
      pageNumber: 1,
    };
    setRfmPage(1);
    fetchRFM();
  };

  const handleRFMReset = () => {
    rfmFilters.current = {
      startDate: defaultStartDate(),
      endDate: new Date(),
      recency: null,
      frequency: null,
      monetary: null,
      recencyScore: null,
      frequencyScore: null,
      monetaryScore: null,
      rfmTotal: null,
      customerSegment: null,
      pageSize: rfmPageSize,
      pageNumber: 1,
      sortBy: "RFMTotal",
      sortOrder: "DESC",
    };
    setRfmPage(1);
    setRfmSortField("rfmTotal");
    setRfmSortOrder(-1);
    fetchRFM();
  };

  const handleRFMPageChange = (event: DataTableStateEvent) => {
    const newPage = Math.floor((event.first || 0) / (event.rows || DEFAULT_PAGE_SIZE)) + 1;
    const newSize = event.rows || DEFAULT_PAGE_SIZE;
    setRfmPage(newPage);
    setRfmPageSize(newSize);
    rfmFilters.current = {
      ...rfmFilters.current,
      pageNumber: newPage,
      pageSize: newSize,
    };
    fetchRFM();
  };

  const handleRFMSort = (field: string, order: string) => {
    setRfmSortField(field);
    setRfmSortOrder(order === "ASC" ? 1 : -1);
    rfmFilters.current = {
      ...rfmFilters.current,
      sortBy: toSpSortBy(field),
      sortOrder: order,
      pageNumber: 1,
    };
    setRfmPage(1);
    fetchRFM();
  };

  // Churn handlers
  const handleChurnSearch = (filters: {
    startDate: Date;
    endDate: Date;
    daysSinceLastOrder: number | null;
  }) => {
    churnFilters.current = {
      ...churnFilters.current,
      ...filters,
      pageNumber: 1,
    };
    setChurnPage(1);
    fetchChurn();
  };

  const handleChurnReset = () => {
    churnFilters.current = {
      startDate: defaultStartDate(),
      endDate: new Date(),
      daysSinceLastOrder: 60,
      pageSize: churnPageSize,
      pageNumber: 1,
      sortBy: "LifetimeRevenue",
      sortOrder: "DESC",
    };
    setChurnPage(1);
    setChurnSortField("lifetimeRevenue");
    setChurnSortOrder(-1);
    fetchChurn();
  };

  const handleChurnPageChange = (event: DataTableStateEvent) => {
    const newPage = Math.floor((event.first || 0) / (event.rows || DEFAULT_PAGE_SIZE)) + 1;
    const newSize = event.rows || DEFAULT_PAGE_SIZE;
    setChurnPage(newPage);
    setChurnPageSize(newSize);
    churnFilters.current = {
      ...churnFilters.current,
      pageNumber: newPage,
      pageSize: newSize,
    };
    fetchChurn();
  };

  const handleChurnSort = (field: string, order: string) => {
    setChurnSortField(field);
    setChurnSortOrder(order === "ASC" ? 1 : -1);
    churnFilters.current = {
      ...churnFilters.current,
      sortBy: toSpSortBy(field),
      sortOrder: order,
      pageNumber: 1,
    };
    setChurnPage(1);
    fetchChurn();
  };

  // Export handler
  const handleExport = () => {
    if (activeTab === 0 && paretoData.length > 0) {
      exportParetoAnalysis(paretoData);
    } else if (activeTab === 1 && rfmData.length > 0) {
      exportRFMSegmentation(rfmData);
    } else if (activeTab === 2 && churnData.length > 0) {
      exportHighValueAtRisk(churnData);
    } else {
      toast.current?.show({
        severity: "warn",
        summary: "No Data",
        detail: "No data available to export",
        life: 3000,
      });
    }
  };

  const getActiveDataLength = () => {
    if (activeTab === 0) return paretoData.length;
    if (activeTab === 1) return rfmData.length;
    if (activeTab === 2) return churnData.length;
    return 0;
  };

  return (
    <div className="layout-content mt-3 mb-3 grid grid-table-mobile ca-container">
      <Toast ref={toast} />

      <div className="col-12">
        <div className="ca-header">
          <div className="ca-header-content">
            <h4 className="ca-header-title">Customer Analytics</h4>
            <p className="ca-header-subtitle">
              Identify high-value customers, segment by behavior, and flag churn risks
            </p>
          </div>
          <FileActionButton action="export"
            label="Export"
            icon="pi pi-file-export"
            disabled={getActiveDataLength() === 0}
            onClick={handleExport}
          />
        </div>
      </div>

      <div className="col-12">
        <TabView
          activeIndex={activeTab}
          onTabChange={(e) => handleTabChange(e.index)}
          className="ca-tabview"
          renderActiveOnly={false}
        >
          <TabPanel header="Pareto Analysis" leftIcon="pi pi-chart-pie mr-2">
            <div className="grid">
              <div className="col-12">
                <div className="ca-tab-info ca-tab-pareto">
                  <div className="ca-tab-intro">
                    <span className="ca-tab-intro-icon pi pi-chart-pie" />
                    <p className="ca-tab-description">
                      <strong>The 80/20 Rule</strong> &mdash; A small group of customers often drives most of your revenue.
                      Ranked by total spending, each customer is assigned a decile (D1 = top 10%, D10 = bottom 10%).
                    </p>
                  </div>
                  <div className="ca-column-chips">
                    <span className="ca-column-chip"><span className="ca-chip-dot" /><span className="ca-chip-label">Revenue</span> total spent</span>
                    <span className="ca-column-chip"><span className="ca-chip-dot" /><span className="ca-chip-label">Avg Order</span> avg per order</span>
                    <span className="ca-column-chip"><span className="ca-chip-dot" /><span className="ca-chip-label">Days Inactive</span> since last order</span>
                    <span className="ca-column-chip"><span className="ca-chip-dot" /><span className="ca-chip-label">Cumulative %</span> running revenue share</span>
                    <span className="ca-column-chip"><span className="ca-chip-dot" /><span className="ca-chip-label">Decile</span> rank 1-10 (D1 = best)</span>
                  </div>
                </div>
              </div>
              <ParetoSearch
                onSearch={handleParetoSearch}
                onReset={handleParetoReset}
              />
              <ParetoDataTable
                data={paretoData}
                pageNumber={paretoPage}
                pageSize={paretoPageSize}
                totalRecords={paretoTotal}
                sortField={paretoSortField}
                sortOrder={paretoSortOrder}
                onPageChange={handleParetoPageChange}
                onSortChange={handleParetoSort}
                refreshData={fetchPareto}
              />
            </div>
          </TabPanel>

          <TabPanel header="RFM Segmentation" leftIcon="pi pi-users mr-2">
            <div className="grid">
              <div className="col-12">
                <div className="ca-tab-info ca-tab-rfm">
                  <div className="ca-tab-intro">
                    <span className="ca-tab-intro-icon pi pi-users" />
                    <p className="ca-tab-description">
                      <strong>Recency, Frequency, Monetary</strong> &mdash; Customers scored 1-5 on how recently they bought (R),
                      how often (F), and how much (M), then placed into actionable segments.
                    </p>
                  </div>
                  <div className="ca-column-chips">
                    <span className="ca-column-chip"><span className="ca-chip-dot" /><span className="ca-chip-label">Recency</span> days since last order</span>
                    <span className="ca-column-chip"><span className="ca-chip-dot" /><span className="ca-chip-label">Frequency</span> total orders</span>
                    <span className="ca-column-chip"><span className="ca-chip-dot" /><span className="ca-chip-label">Monetary</span> total spent</span>
                    <span className="ca-column-chip"><span className="ca-chip-dot" /><span className="ca-chip-label">R / F / M</span> scores 1-5 (5 = best)</span>
                    <span className="ca-column-chip"><span className="ca-chip-dot" /><span className="ca-chip-label">RFM Total</span> combined out of 15</span>
                  </div>
                  <div className="ca-segment-guide">
                    <span className="ca-segment-guide-title">Segments:</span>
                    <div className="ca-segment-chips">
                      <span className="ca-segment-chip" style={{ background: "#22c55e", color: "#fff" }}>Champions <span className="ca-segment-hint">High R, F &amp; M &mdash; your best customers</span></span>
                      <span className="ca-segment-chip" style={{ background: "#3b82f6", color: "#fff" }}>Loyal Customers <span className="ca-segment-hint">Buy often with good spend</span></span>
                      <span className="ca-segment-chip" style={{ background: "#f59e0b", color: "#1e293b" }}>New Customers <span className="ca-segment-hint">Recent first-time buyers</span></span>
                      <span className="ca-segment-chip" style={{ background: "#ef4444", color: "#fff" }}>At Risk - High Value <span className="ca-segment-hint">Big spenders going inactive</span></span>
                      <span className="ca-segment-chip" style={{ background: "#fca5a5", color: "#1e293b" }}>At Risk - Low Value <span className="ca-segment-hint">Low spenders drifting away</span></span>
                      <span className="ca-segment-chip" style={{ background: "#a855f7", color: "#fff" }}>Big Spenders (Infrequent) <span className="ca-segment-hint">High spend, few orders</span></span>
                      <span className="ca-segment-chip" style={{ background: "#94a3b8", color: "#fff" }}>Lost Customers <span className="ca-segment-hint">No orders in a long time</span></span>
                      <span className="ca-segment-chip" style={{ background: "#f97316", color: "#fff" }}>Need Attention <span className="ca-segment-hint">Moderate value, slowing down</span></span>
                    </div>
                  </div>
                </div>
              </div>
              <RFMSearch
                onSearch={handleRFMSearch}
                onReset={handleRFMReset}
              />
              <RFMDataTable
                data={rfmData}
                pageNumber={rfmPage}
                pageSize={rfmPageSize}
                totalRecords={rfmTotal}
                sortField={rfmSortField}
                sortOrder={rfmSortOrder}
                onPageChange={handleRFMPageChange}
                onSortChange={handleRFMSort}
                refreshData={fetchRFM}
              />
            </div>
          </TabPanel>

          <TabPanel header="Churn Risk" leftIcon="pi pi-exclamation-triangle mr-2">
            <div className="grid">
              <div className="col-12">
                <div className="ca-tab-info ca-tab-churn">
                  <div className="ca-tab-intro">
                    <span className="ca-tab-intro-icon pi pi-exclamation-triangle" />
                    <p className="ca-tab-description">
                      <strong>High-value customers going quiet</strong> &mdash; Customers who spent above average but haven't ordered recently.
                      A personal call or tailored offer could recover significant revenue.
                    </p>
                  </div>
                  <div className="ca-column-chips">
                    <span className="ca-column-chip"><span className="ca-chip-dot" /><span className="ca-chip-label">Lifetime Revenue</span> total across all orders</span>
                    <span className="ca-column-chip"><span className="ca-chip-dot" /><span className="ca-chip-label">Avg Order</span> typical spend per order</span>
                    <span className="ca-column-chip"><span className="ca-chip-dot" /><span className="ca-chip-label">Days Inactive</span> color: &gt;90 red, &gt;60 orange, &gt;30 yellow</span>
                    <span className="ca-column-chip"><span className="ca-chip-dot" /><span className="ca-chip-label">Orders</span> total completed</span>
                  </div>
                </div>
              </div>
              <ChurnSearch
                onSearch={handleChurnSearch}
                onReset={handleChurnReset}
              />
              <ChurnDataTable
                data={churnData}
                pageNumber={churnPage}
                pageSize={churnPageSize}
                totalRecords={churnTotal}
                sortField={churnSortField}
                sortOrder={churnSortOrder}
                onPageChange={handleChurnPageChange}
                onSortChange={handleChurnSort}
                refreshData={fetchChurn}
              />
            </div>
          </TabPanel>
        </TabView>
      </div>

      {loading && <AppLoading />}
    </div>
  );
};

export default CustomerAnalyticsMain;
